import { db, CallFoundryDexie } from '../db';

const DEVICE_ID_KEY = 'callfoundry_device_id';
const DEVICE_SEQ_KEY = 'callfoundry_device_sequence';

function generateUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Standard RFC4122 v4 fallback
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Returns the persistent device UUID.
 * Generated once per client installation and stored in localStorage.
 */
export function getDeviceId(): string {
  try {
    let deviceId = localStorage.getItem(DEVICE_ID_KEY);
    if (!deviceId) {
      deviceId = generateUUID();
      localStorage.setItem(DEVICE_ID_KEY, deviceId);
    }
    return deviceId;
  } catch {
    return 'fallback-device-id-0000-0000';
  }
}

/**
 * Atomically increments and returns the next monotonic sequence number for this device
 * using an ACID transaction on the Dexie `deviceMetadata` table.
 * Guaranteed to prevent race conditions across concurrent operations and tabs.
 */
export async function getAtomicDeviceSequence(database: CallFoundryDexie = db): Promise<number> {
  return database.transaction('rw', database.deviceMetadata, async () => {
    const record = await database.deviceMetadata.get('deviceSequence');
    const currentSeq = typeof record?.value === 'number' ? record.value : 0;
    const nextSeq = currentSeq + 1;
    await database.deviceMetadata.put({
      key: 'deviceSequence',
      value: nextSeq,
      updatedAt: new Date().toISOString(),
    });
    // Also keep localStorage updated for diagnostics inspection
    try {
      localStorage.setItem(DEVICE_SEQ_KEY, nextSeq.toString());
    } catch {
      // ignore
    }
    return nextSeq;
  });
}

/**
 * Synchronous fallback / test helper for sequence generation.
 */
export function getNextDeviceSequence(): number {
  try {
    const raw = localStorage.getItem(DEVICE_SEQ_KEY);
    const current = raw ? parseInt(raw, 10) : 0;
    const next = isNaN(current) ? 1 : current + 1;
    localStorage.setItem(DEVICE_SEQ_KEY, next.toString());
    return next;
  } catch {
    return 1;
  }
}

/**
 * Utility for tests only: resets persistent device sequence and ID.
 */
export async function resetDeviceIdentityForTesting(database?: CallFoundryDexie): Promise<void> {
  try {
    localStorage.removeItem(DEVICE_ID_KEY);
    localStorage.removeItem(DEVICE_SEQ_KEY);
  } catch {
    // ignore
  }
  if (database) {
    try {
      await database.deviceMetadata.delete('deviceSequence');
    } catch {
      // ignore
    }
  }
}
